import { useEffect, useState } from 'react';
import type { ProviderInfo } from '~/types/model';
import { createScopedLogger } from '~/utils/logger';
import { $accessToken, setAuth } from '~/lib/stores/auth';
import { apiRefreshToken } from '~/lib/api/auth';
import { ENCODED_JSON_CONTENT_TYPE, encodeJsonBody } from '~/lib/encoded-json';

const logger = createScopedLogger('usePromptEnhancement');

function fetchEnhancer(body: string) {
  const token = $accessToken.get();
  return fetch('/api/enhancer', {
    method: 'POST',
    headers: {
      // Base64 JSON — a pasted prompt can carry code the edge firewall 403s (see encoded-json).
      'Content-Type': ENCODED_JSON_CONTENT_TYPE,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
  });
}

export function usePromptEnhancer() {
  const [enhancingPrompt, setEnhancingPrompt] = useState(false);
  const [promptEnhanced, setPromptEnhanced] = useState(false);

  // Shown inline in the composer (not a toast) — feedback appears right where
  // the user clicked. Auto-dismisses below.
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  useEffect(() => {
    if (!enhanceError) {
      return undefined;
    }

    const timeout = setTimeout(() => setEnhanceError(null), 6000);

    return () => clearTimeout(timeout);
  }, [enhanceError]);

  const clearEnhanceError = () => setEnhanceError(null);

  const resetEnhancer = () => {
    setEnhancingPrompt(false);
    setPromptEnhanced(false);
  };

  const enhancePrompt = async (
    input: string,
    setInput: (value: string) => void,
    model: string,
    provider: ProviderInfo,
  ) => {
    setEnhancingPrompt(true);
    setPromptEnhanced(false);
    setEnhanceError(null);

    const originalInput = input;

    const requestBody: any = {
      message: input,
      model,
      provider,
    };

    const body = encodeJsonBody(requestBody);

    try {
      let response = await fetchEnhancer(body);

      if (response.status === 401) {
        try {
          const { accessToken, user } = await apiRefreshToken();
          setAuth(accessToken, user);
          response = await fetchEnhancer(body);
        } catch {
          logger.error('Token refresh failed');
        }
      }

      if (!response.ok) {
        let errorCode: string | undefined;

        try {
          errorCode = ((await response.json()) as any)?.code;
        } catch {
          // non-JSON error body — fall through to the generic message
        }

        if (response.status === 402 || errorCode === 'CREDITS_EXHAUSTED') {
          setEnhanceError('Out of credits — your prompt was left unchanged.');
        } else if (response.status === 401) {
          setEnhanceError('Session expired — sign in again to enhance prompts.');
        } else {
          setEnhanceError("Couldn't enhance the prompt — your text is unchanged.");
        }

        return;
      }

      const reader = response.body?.getReader();

      if (!reader) {
        setEnhanceError("Couldn't enhance the prompt — your text is unchanged.");
        return;
      }

      const decoder = new TextDecoder();

      let _input = '';

      try {
        setInput('');

        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            break;
          }

          _input += decoder.decode(value);

          logger.trace('Set input', _input);

          setInput(_input);
        }
      } catch (error) {
        logger.error(error);
        setInput(originalInput);
        setEnhanceError('Enhancement was interrupted — your original prompt was restored.');

        return;
      }

      if (!_input.trim()) {
        // Upstream failed mid-stream (e.g. provider error) and sent nothing back
        setInput(originalInput);
        setEnhanceError("Couldn't enhance the prompt — your original prompt was restored.");

        return;
      }

      setPromptEnhanced(true);

      // Re-set after state flush so the final text wins over any pending updates
      setTimeout(() => {
        setInput(_input);
      });
    } catch (error) {
      logger.error(error);
      setInput(originalInput);
      setEnhanceError("Couldn't reach the server — your text is unchanged.");
    } finally {
      setEnhancingPrompt(false);
    }
  };

  return { enhancingPrompt, promptEnhanced, enhancePrompt, resetEnhancer, enhanceError, clearEnhanceError };
}
